import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Loader2, LogOut, RotateCcw, Save, ExternalLink } from "lucide-react";
import { CMS_DEFAULTS, mergeDeep } from "@/components/settld/i18n";
import { SectionEditor } from "./admin/SectionEditor";
import { AdminLogin } from "./admin/AdminLogin";
import { SectionsToggles } from "./admin/SectionsToggles";
import { EmailSettings } from "./admin/EmailSettings";
import { ImageEditor } from "./admin/ImageEditor";
import { LeadsTab } from "./admin/LeadsTab";
import { ConciergeBrain } from "./admin/ConciergeBrain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  ["hero", "Hero"], ["nav", "Navigation"], ["dash", "Dashboard Card"], ["marquee", "Marquee"],
  ["problem", "Problem"], ["hiw", "How It Works"], ["platform", "Platform"], ["comparison", "Comparison"],
  ["pricing", "Pricing"], ["testimonials", "Testimonials"], ["trust", "Trust"], ["quote", "Quote Form"],
  ["concierge", "AI Concierge"], ["footer", "Footer"],
  ["leads", "Leads"], ["visibility", "Sections On/Off"], ["images", "Images"], ["brain", "Concierge Brain"], ["settings", "Email Settings"],
];

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("settld-admin-token") || "");
  const [doc, setDoc] = useState(null);
  const [section, setSection] = useState("hero");
  const [editLang, setEditLang] = useState("en");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/content`)
      .then((r) => setDoc(mergeDeep(CMS_DEFAULTS, r.data?.content || {})))
      .catch(() => toast.error("Could not load content"));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { content: doc }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Content saved — live on the site now.");
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("settld-admin-token");
        setToken("");
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL content and images to the original defaults?")) return;
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { content: {} }, { headers: { Authorization: `Bearer ${token}` } });
      setDoc(mergeDeep(CMS_DEFAULTS, {}));
      toast.success("Content reset to defaults.");
    } catch {
      toast.error("Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("settld-admin-token");
    setToken("");
  };

  if (!token) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <AdminLogin onLogin={setToken} />
      </>
    );
  }

  if (!doc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Loader2 size={28} className="animate-spin text-navy" />
      </div>
    );
  }

  const isImages = section === "images";
  const isVisibility = section === "visibility";
  const isSettings = section === "settings";
  const isLeads = section === "leads";
  const isBrain = section === "brain";
  const isTextSection = !isImages && !isVisibility && !isSettings && !isLeads && !isBrain;

  return (
    <div data-testid="admin-cms" className="min-h-screen bg-paper font-sans text-ink">
      <Toaster position="top-center" richColors />
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo-mark-badge.png" alt="Settld" className="h-9 w-9 rounded-lg" />
            <span className="font-sans text-lg font-extrabold tracking-tight">
              Settld<span className="text-aqua-500">.</span> <span className="text-xs font-bold text-slate-400">Admin CMS</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a data-testid="admin-view-site-btn" href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-navy hover:text-navy">
              <ExternalLink size={13} /> View Site
            </a>
            <button data-testid="admin-reset-btn" onClick={reset} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-maple hover:text-maple">
              <RotateCcw size={13} /> Reset
            </button>
            <button data-testid="admin-save-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-full bg-maple px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-[#C23225] disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save &amp; Publish
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-maple hover:text-maple" aria-label="Log out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-8 lg:grid-cols-[220px_1fr]">
        <aside className="flex gap-1 overflow-x-auto lg:flex-col lg:overflow-visible">
          {SECTIONS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-section-tab-${key}`}
              onClick={() => setSection(key)}
              className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-left text-sm font-semibold transition-colors ${
                section === key ? "bg-navy text-white" : "text-slate-500 hover:bg-white hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </aside>

        <main className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
          {isTextSection && (
            <div className="mb-6 flex items-center justify-between">
              <h1 className="font-display text-2xl font-semibold">{SECTIONS.find(([k]) => k === section)?.[1]}</h1>
              <div className="flex items-center rounded-full border border-slate-200 p-0.5">
                {["en", "fr"].map((l) => (
                  <button
                    key={l}
                    data-testid={`admin-lang-${l}`}
                    onClick={() => setEditLang(l)}
                    className={`rounded-full px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                      editLang === l ? "bg-navy text-white" : "text-slate-400 hover:text-ink"
                    }`}
                  >
                    {l === "en" ? "English" : "Français"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isImages && <ImageEditor doc={doc} setDoc={setDoc} token={token} />}
          {isVisibility && <SectionsToggles doc={doc} setDoc={setDoc} />}
          {isSettings && <EmailSettings token={token} />}
          {isLeads && <LeadsTab token={token} />}
          {isBrain && <ConciergeBrain token={token} />}
          {isTextSection && (
            <SectionEditor
              value={doc[editLang][section]}
              onChange={(v) => setDoc({ ...doc, [editLang]: { ...doc[editLang], [section]: v } })}
            />
          )}
        </main>
      </div>
    </div>
  );
}
